import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

/**
 * /set-password — landing page for the welcome / password-setup email.
 *
 * The welcome email links here with `?token_hash=<recovery token>` (minted by
 * generateRecoveryLink in the Netlify functions). We verify it in-app via
 * supabase.auth.verifyOtp — deliberately NOT the GoTrue redirect flow, whose
 * Site-URL fallback on this shared Supabase project sent new users to the
 * alamedapointbg.com gateway instead of password setup.
 *
 * Also tolerates the legacy link shape (#access_token=…&type=recovery in the
 * URL hash) from emails sent before this page existed, and lets an
 * already-signed-in user change their password.
 */

type Phase = 'verifying' | 'ready' | 'expired';

const PASSWORD_RULES =
  'Use at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a symbol (e.g. !@#$%).';

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tokenHash = params.get('token_hash');

  const [phase, setPhase] = useState<Phase>('verifying');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // verifyOtp consumes the one-time token — StrictMode double-mounts effects
  // in dev, so guard against running it twice.
  const verifyStarted = useRef(false);

  useEffect(() => {
    if (verifyStarted.current) return;
    verifyStarted.current = true;
    let cancelled = false;

    async function establishSession() {
      // 1. Preferred: token_hash from the welcome email.
      if (tokenHash) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          type: 'recovery',
          token_hash: tokenHash,
        });
        if (cancelled) return;
        setPhase(verifyError ? 'expired' : 'ready');
        return;
      }

      // 2. Legacy: implicit-flow tokens in the URL hash (emails sent before
      // this page existed; the client has detectSessionInUrl off).
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');
      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (cancelled) return;
        if (!sessionError) {
          window.history.replaceState(null, '', window.location.pathname);
          setPhase('ready');
          return;
        }
      }

      // 3. Already signed in (e.g. navigated here by hand) — allow a change.
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      setPhase(data.session ? 'ready' : 'expired');
    }

    void establishSession();
    return () => {
      cancelled = true;
    };
  }, [tokenHash]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // Mirror the Supabase project's password policy (shared with the gateway):
    // min 8 chars with at least one lowercase, uppercase, digit and symbol.
    if (
      password.length < 8 ||
      !/[a-z]/.test(password) ||
      !/[A-Z]/.test(password) ||
      !/[0-9]/.test(password) ||
      !/[^A-Za-z0-9]/.test(password)
    ) {
      setError(PASSWORD_RULES);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (updateError) {
      setError(
        updateError.message.toLowerCase().includes('password')
          ? PASSWORD_RULES
          : updateError.message,
      );
      return;
    }
    navigate('/home', { replace: true });
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-brix-surface flex items-center justify-center px-4 py-10">
      {/* Same navy-bloom backdrop as /login. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 45% at 50% 30%, rgba(31,78,121,0.10), transparent 70%), radial-gradient(ellipse 35% 25% at 85% 90%, rgba(244,180,0,0.06), transparent 60%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(31,78,121,1) 1px, transparent 1px), linear-gradient(90deg, rgba(31,78,121,1) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex justify-start">
          <BrixLogo variant="full" size="lg" showTagline showTrademark />
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-brix-border bg-brix-surface/85 backdrop-blur-xl shadow-card-deep">
          <div className="h-1.5 bg-brix-primary" aria-hidden />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-1.5 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
          />

          <div className="p-6">
            {phase === 'verifying' && (
              <div className="py-10 text-center">
                <Loader2 size={28} className="animate-spin text-brix-primary mx-auto mb-3" />
                <p className="text-sm text-brix-muted">Checking your setup link…</p>
              </div>
            )}

            {phase === 'expired' && (
              <>
                <h1 className="font-display text-2xl font-semibold mb-1 text-brix-ink">
                  This link has expired
                </h1>
                <p className="text-sm text-brix-muted mb-6 leading-relaxed">
                  Password setup links can only be used once and expire after a
                  while. Ask your account manager to resend your welcome email,
                  or call us at{' '}
                  <a
                    href={SUPPORT_TEL_HREF}
                    className="text-brix-primary font-semibold no-underline"
                  >
                    {SUPPORT_PHONE}
                  </a>{' '}
                  and we&rsquo;ll send a fresh one.
                </p>
                <Link to="/login">
                  <Button size="lg" className="w-full">
                    Go to sign in
                  </Button>
                </Link>
              </>
            )}

            {phase === 'ready' && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 size={20} className="text-brix-primary" />
                  <h1 className="font-display text-2xl font-semibold text-brix-ink">
                    Create your password
                  </h1>
                </div>
                <p className="text-sm text-brix-muted mb-6">
                  Welcome to Brix Order. Choose a password to finish setting up
                  your account.
                </p>

                <form onSubmit={onSubmit} className="space-y-4" noValidate>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password">New password</Label>
                    <PasswordInput
                      id="new-password"
                      autoComplete="new-password"
                      autoFocus
                      className="bg-brix-surface/80 backdrop-blur-sm"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <p className="text-[11px] text-brix-muted">{PASSWORD_RULES}</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <PasswordInput
                      id="confirm-password"
                      autoComplete="new-password"
                      className="bg-brix-surface/80 backdrop-blur-sm"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>

                  {error && (
                    <div
                      role="alert"
                      className="rounded-lg border border-brix-border bg-brix-primary-tint/80 backdrop-blur-sm px-3 py-2"
                    >
                      <p className="text-xs text-brix-primary">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full shadow-card hover:shadow-card-hover active:scale-[0.98] transition-all"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Saving…
                      </>
                    ) : (
                      'Set password & sign in'
                    )}
                  </Button>
                </form>

                <p className="mt-6 text-[11px] text-brix-muted leading-relaxed">
                  Trouble? Call us at{' '}
                  <a
                    href={SUPPORT_TEL_HREF}
                    className="underline text-brix-primary hover:text-brix-ink transition-colors duration-200"
                  >
                    {SUPPORT_PHONE}
                  </a>
                  .
                </p>
              </>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-[10px] text-brix-muted">
          Brix Beverage · Alameda, CA · 1951 Monarch St
        </p>
      </div>
    </div>
  );
}
